import { useEffect, useMemo, useState } from 'react'
import useSWR, { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import dayjs from 'dayjs'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PRODUCT_PACKAGES_URL } from '@/components/product-packages-loader'
import { putFetcher } from '@/hooks'
import { useRoles } from '@/hooks/auth'
import { useToast } from '@/hooks/use-toast'
import { Role } from '@/constants/role'

const ADMIN_URL = '/product-packages/admin'
const ALIAS_MAX_LENGTH = 128

/** 下级 App 别名配置：只能改别名，code 是查询参数不可改 */
const PackageAliasPage = () => {
  const canEdit = useRoles([Role.BuyerLeader, Role.RevenuerLeader])
  const { data, isLoading, error } = useSWR(canEdit ? [ADMIN_URL] : null)

  const groups = useMemo(() => {
    const map = new Map()
    ;(Array.isArray(data) ? data : []).forEach((row) => {
      const list = map.get(row.pid) ?? []
      list.push(row)
      map.set(row.pid, list)
    })
    return [...map.entries()].sort(([a], [b]) => a - b)
  }, [data])

  if (!canEdit) {
    return <div className="text-muted-foreground">仅 Admin、投放负责人、变现负责人可配置下级 App 别名</div>
  }
  if (isLoading) return <div className="text-muted-foreground">加载中...</div>
  if (error) {
    return <div className="text-destructive">加载失败：{error.message}</div>
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        别名只影响页面展示；code 是查询参数，不可修改。保存后各页面下拉即时更新。
      </p>
      {groups.map(([pid, rows]) => (
        <section key={pid} className="space-y-2">
          <h2 className="text-lg font-semibold">pid {pid}</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">pid</TableHead>
                <TableHead className="w-40">code</TableHead>
                <TableHead>别名</TableHead>
                <TableHead className="w-16">排序</TableHead>
                <TableHead className="w-16">启用</TableHead>
                <TableHead className="w-56">最近修改</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <AliasRow key={row.id} row={row} />
              ))}
            </TableBody>
          </Table>
        </section>
      ))}
    </div>
  )
}

const AliasRow = ({ row }) => {
  const { toast } = useToast()
  const [alias, setAlias] = useState(row.alias)
  const { trigger, isMutating } = useSWRMutation(
    `/product-packages/${row.id}`,
    putFetcher,
  )

  useEffect(() => {
    setAlias(row.alias)
  }, [row.alias])

  const trimmed = alias.trim()
  const canSave =
    !isMutating &&
    trimmed !== '' &&
    trimmed.length <= ALIAS_MAX_LENGTH &&
    trimmed !== row.alias

  const handleSave = async () => {
    if (!canSave) return
    try {
      await trigger({ alias: trimmed })
      mutate([ADMIN_URL])
      mutate([PRODUCT_PACKAGES_URL])
      toast({ title: `已保存 ${row.code} 的别名` })
    } catch (e) {
      toast({
        title: '保存失败',
        description: e.message,
        variant: 'destructive',
      })
    }
  }

  return (
    <TableRow>
      <TableCell>{row.pid}</TableCell>
      <TableCell className="font-mono">{row.code}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Input
            className="h-8 max-w-xs"
            value={alias}
            maxLength={ALIAS_MAX_LENGTH}
            onChange={(e) => setAlias(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave()
              if (e.key === 'Escape') setAlias(row.alias)
            }}
          />
          <Button size="sm" disabled={!canSave} onClick={handleSave}>
            {isMutating ? '保存中...' : '保存'}
          </Button>
        </div>
      </TableCell>
      <TableCell>{row.sort}</TableCell>
      <TableCell>
        {Number(row.enabled) === 1 || row.enabled === true ? (
          <Badge variant="outline">启用</Badge>
        ) : (
          <Badge variant="secondary">停用</Badge>
        )}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {row.updatedBy || '-'}
        {row.updatedAt && ` · ${dayjs(row.updatedAt).format('YYYY-MM-DD HH:mm')}`}
      </TableCell>
    </TableRow>
  )
}

export default PackageAliasPage
